/* eslint-disable i18next/no-literal-string */
import React, { ReactElement, useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import styles from "./select.module.scss";
import classnames from "classnames";
import type { AsyncSelectProps, BaseSelectProps, CreateableAsyncSelectProps, CreateableSelectProps, SelectOption, SelectProps } from "@whatslly/types/UI-V2/BaseElements/SelectType";
import globalStyles from "../../globalStyles";
import omit from "lodash/omit";
import ReactSelect, {
  StylesConfig,
  OptionProps,
  ControlProps,
  DropdownIndicatorProps,
  MenuProps,
  SingleValueProps,
  GroupBase,
  OnChangeValue,
  ActionMeta,
  InputProps,
  components as ReactSelectComponents,
  NoticeProps,
  MultiValueGenericProps,
  SelectInstance,
  components,
  MultiValueRemoveProps,
} from "react-select";
import { Svg } from "../Svg";
import { AsyncPaginate, withAsyncPaginate } from "react-select-async-paginate";
import CreatableSelect, { CreatableProps } from "react-select/creatable";

import type { UseAsyncPaginateParams, ComponentProps } from "react-select-async-paginate";
import { randomString } from "@whatslly/commons/utils";
import { useTranslation } from "react-i18next";
import { FormatOptionLabelMeta } from "react-select/dist/declarations/src/Select";

type AsyncPaginateCreatableProps<OptionType, Group extends GroupBase<OptionType>, Additional, IsMulti extends boolean> = CreatableProps<
  OptionType,
  IsMulti,
  Group
> &
  UseAsyncPaginateParams<OptionType, Group, Additional> &
  ComponentProps<OptionType, Group, IsMulti>;

type AsyncPaginateCreatableType = <OptionType, Group extends GroupBase<OptionType>, Additional, IsMulti extends boolean = false>(
  props: AsyncPaginateCreatableProps<OptionType, Group, Additional, IsMulti>
) => ReactElement;
/**
 * Please do not use this component, use one of the select components from the list below
 *
 * {@link Select} - normal select the get options in type form of {@link SelectOption};
 *
 * {@link AsyncSelect} - select component that get options from the server or another function by using {@link AsyncSelectProps.loadOptionsFromServerFunction}
 *
 * {@link CreateableSelect} - select component that get options - {@link SelectOption} and onCreateOption function to add new options (if onCreateOption is undefined the created value will be selected as the new value)
 *
 * {@link CreateableAsyncSelect} - a mix between {@link AsyncSelect} & {@link CreateableSelect} that let you get data from the server and create new values
 *
 */
export const BaseSelectDoNotUse = <
  OptionValueType,
  IsMulti extends boolean = false,
  Option extends SelectOption<OptionValueType> = SelectOption<OptionValueType>,
>({
  padding,
  paddingInline,
  paddingTop,
  paddingRight,
  paddingBottom,
  paddingLeft,
  borderRadius,
  borderColor,
  margin,
  marginTop,
  marginRight,
  marginBottom,
  marginLeft,
  backgroundColor,
  selectSize,
  color,
  onChange,
  onBlur,
  isMulti,
  loadOptionsFromServerFunction,
  options,
  showClear,
  value,
  isAsync,
  onCreateOption,
  createLabel,
  createLabelIconColor,
  createLabelIcon,
  formatCreateLabel,
  minimumLettersToSearch,
  customNoResultElement,
  isSearchable,
  isValidNewOption,
  disabled,
  isCraeteable,
  defaultValue,
  dontHighlightSearchWord,
  menuBottomText,
  placeholder,
  menuClassName,
  hideDropdownIndicator,
  controlClassName,
  menuWidth,
  CustomOptionComponent,
  name,
  ignoreMenuDirection,
  classNames,
  ...mainProps
}: BaseSelectProps<OptionValueType, IsMulti, Option>) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks, @typescript-eslint/no-explicit-any
  const AsyncPaginateCreatable = useMemo(() => {
    return withAsyncPaginate(CreatableSelect) as AsyncPaginateCreatableType;
  }, []);
  const dropdownIndicatorIdRef = useRef(randomString(10));
  const reactSelectRef = useRef<SelectInstance<Option, IsMulti, GroupBase<Option>>>(null);
  const { t } = useTranslation();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const testIdMainProps = useMemo(() => mainProps["data-testid"], [mainProps["data-testid"]]);
  const Control = useCallback(
    (props: ControlProps<Option, IsMulti>) => {
      const { children, className, getStyles, innerRef, innerProps } = props;

      return (
        <div
          ref={innerRef}
          style={omit(getStyles("control", props), "backgroundColor", "borderRadius", "padding", "height", "boxShadow", "minHeight")}
          className={classnames(
            className,
            styles.control,
            globalStyles[`br-${borderRadius ?? "default"}`],
            globalStyles[`bg-color-${backgroundColor ?? "main-secondary"}`],
            globalStyles[`bc-${borderColor ?? "neutral-secondary"}`],
            globalStyles[`select-${selectSize ?? "default"}`],
            globalStyles["v2ElementContainer"],
            controlClassName
          )}
          data-testid={`${testIdMainProps}-selected`}
          {...innerProps}
        >
          {children}
        </div>
      );
    },
    [backgroundColor, borderColor, borderRadius, controlClassName, testIdMainProps, selectSize]
  );

  const DropdownIndicator = useCallback(
    (props: DropdownIndicatorProps<Option, IsMulti>) => {
      const { className, getStyles, innerProps } = props;
      if (hideDropdownIndicator) {
        return <></>;
      }
      return (
        <div style={omit(getStyles("dropdownIndicator", props))} className={classnames(className, styles.indicator)} {...innerProps}>
          <Svg
            id={dropdownIndicatorIdRef.current}
            iconName="arrow_down"
            className={classnames(globalStyles[`color-${color ?? "main-primary"}`])}
            height={9}
            width={14}
          />
        </div>
      );
    },
    [color, hideDropdownIndicator]
  );

  const OptionComponent = useCallback(
    (props: OptionProps<Option, IsMulti>) => {
      const { children, className, getStyles, innerRef, isFocused, innerProps, isSelected } = props;
      const { dontApplyOptionCss } = props.data;
      if (dontApplyOptionCss) {
        return (
          <div ref={innerRef} {...innerProps}>
            {CustomOptionComponent ? <CustomOptionComponent {...props["data"]} /> : children}
          </div>
        );
      }
      return (
        <div
          className={classnames(
            {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              [styles.newOption]: (props.data as any).__isNew__,
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              [globalStyles["btc-neutral-secondary"]]: (props.data as any).__isNew__,
              [styles.disabled]: (props.data as any).__disabled__,
            },
            styles.optionContainer
          )}
        >
          <div
            ref={innerRef}
            style={omit(getStyles("option", props), "color", "backgroundColor", "cursor", "padding")}
            className={classnames(className, globalStyles[`color-${color ?? "main-primary"}`], styles.option, {
              [styles.focusedOption]: isFocused,
              [styles.selectedOption]: isSelected,
            })}
            data-testid={`select-option`}
            {...innerProps}
          >
            {CustomOptionComponent ? <CustomOptionComponent {...props["data"]} /> : children}
          </div>
        </div>
      );
    },
    [CustomOptionComponent, color]
  );

  const Menu = useCallback(
    (props: MenuProps<Option, IsMulti>) => (
      <CustomMenuComponent
        {...props}
        menuBottomText={menuBottomText}
        menuClassName={menuClassName}
        menuWidth={menuWidth}
        ignoreMenuDirection={ignoreMenuDirection}
      />
    ),
    [ignoreMenuDirection, menuBottomText, menuClassName, menuWidth]
  );

  const SingleValue = useCallback(
    (props: SingleValueProps<Option, IsMulti>) => {
      const { children, className, getStyles, innerProps } = props;
      return (
        <div
          style={omit(getStyles("singleValue", props), "color")}
          className={classnames(className, globalStyles[`color-${color ?? "main-primary"}`], styles.singleValue)}
          {...innerProps}
        >
          {children}
        </div>
      );
    },
    [color]
  );

  const Input = useCallback(
    (props: InputProps<Option, IsMulti>) => {
      const { className, getStyles, ...propsToUse } = props;
      return (
        // @ts-expect-error - react-select types incompatible with React 18
        <ReactSelectComponents.Input
          className={classnames(styles.inputContainerStyles, globalStyles[`color-${color ?? "main-primary"}`], globalStyles["fs-default"], className)}
          inputClassName={classnames(styles.inputStyles, globalStyles[`color-${color ?? "main-primary"}`], globalStyles["fs-default"], className)}
          style={omit(getStyles("input", props), ["color", "margin"])}
          getStyles={getStyles}
          data-testid={testIdMainProps}
          {...propsToUse}
        />
      );
    },
    [color, testIdMainProps]
  );

  const MultiValueContainer = (props: MultiValueGenericProps<Option, IsMulti>) => {
    return (
      <div className={styles.multiValueContainerWrapper}>
        {/* @ts-expect-error - react-select types incompatible with React 18 */}
        <components.MultiValueContainer {...props} />
      </div>
    );
  };

  const MultiValueRemove = (props: MultiValueRemoveProps<Option, IsMulti>) => {
    return (
      <div className={styles.multiValueRemoveWrapper}>
        {/* @ts-expect-error - react-select types incompatible with React 18 */}
        <components.MultiValueRemove {...props} />
      </div>
    );
  };

  const customStyles = useMemo<StylesConfig<Option, IsMulti>>(
    () => ({
      indicatorsContainer: (provided) => {
        return { ...provided, paddingRight: "12px", cursor: "pointer" };
      },
      indicatorSeparator: () => {
        return { display: "none" };
      },
      valueContainer: (provided) => {
        return {
          ...provided,
          marginLeft: "2px",
          cursor: "pointer",
          flexWrap: "wrap",
          rowGap: 4,
          minWidth: 0,
        };
      },
      input: (provided) => {
        return { ...provided };
      },
      menu: (provided) => {
        return {
          ...provided,
          marginTop: "12px",
          boxShadow: "0px 15px 18px rgba(0, 0, 0, 0.1)",
          borderRadius: "12px",
          overflow: "hidden",
          border: "1px solid #D8DDE3",
          padding: "12px",
        };
      },
      placeholder: (provided) => {
        return { ...provided, color: "#667885" };
      },
      menuList: (provided) => {
        return { ...provided, padding: 0 };
      },
      multiValue: (provided) => {
        return { ...provided, borderRadius: "6px", overflow: "visible", maxWidth: "100%", minWidth: 0 };
      },
      multiValueLabel: (provided) => {
        return { ...provided, display: "flex", alignItems: "flex-start" };
      },
      menuPortal: (provided) => {
        return { ...provided, zIndex: 10001 };
      },
    }),
    []
  );

  const formattedLabel = useCallback(
    (data: Option, labelMeta: FormatOptionLabelMeta<Option>) => {
      const { label, icon, iconColor, __isNew__, customIconComponent } = data;
      const optionTestId = data.testId ?? label;
      const testId = `select-option-${optionTestId?.toString().toLowerCase().replaceAll(/\s/g, "_")}`;
      if (typeof label !== "string" || __isNew__ || labelMeta.inputValue === "" || dontHighlightSearchWord) {
        return (
          <>
            {icon && (
              <div className={classnames(styles.iconContainer)}>
                <Svg
                  iconName={icon}
                  height={18}
                  width={18}
                  className={classnames(styles.optionSvg, globalStyles[`color-${iconColor ?? "main-primary"}`])}
                />
              </div>
            )}
            {customIconComponent && <div className={classnames(styles.iconContainer)}>{customIconComponent}</div>}

            <div data-testid={testId} className={classnames(styles.formattedLabel)}>
              {label}
            </div>
          </>
        );
      }
      const highlighted = label.replace(
        new RegExp(labelMeta.inputValue.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), `gi`),
        (highlighted: string) => `<strong>${highlighted}</strong>`
      );
      return (
        <>
          {icon && (
            <div className={classnames(styles.iconContainer)}>
              <Svg
                iconName={icon}
                height={18}
                width={18}
                className={classnames(styles.optionSvg, globalStyles[`color-${iconColor ?? "main-primary"}`])}
              />
            </div>
          )}
          {customIconComponent && <div className={classnames(styles.iconContainer)}>{customIconComponent}</div>}
          <div data-testid={testId} className={classnames(styles.formattedLabel)} dangerouslySetInnerHTML={{ __html: highlighted }} />
        </>
      );
    },
    [dontHighlightSearchWord]
  );

  const NoOptionsMessageElement = useCallback(
    (props: NoticeProps<Option, IsMulti>) => {
      const value = props.selectProps.inputValue;
      if (customNoResultElement) {
        return (
          // @ts-expect-error - react-select types incompatible with React 18
          <ReactSelectComponents.NoOptionsMessage {...props} className={classnames(styles.messageContainer, styles.messageContainerCustom)}>
            {customNoResultElement}
          </ReactSelectComponents.NoOptionsMessage>
        );
      }
      if (minimumLettersToSearch && value.length < minimumLettersToSearch) {
        return (
          // @ts-expect-error - react-select types incompatible with React 18
          <ReactSelectComponents.NoOptionsMessage {...props} className={styles.messageContainer}>
            {t("minimum_letters", { minimumLettersToSearch })}
          </ReactSelectComponents.NoOptionsMessage>
        );
      }

      return (
        // @ts-expect-error - react-select types incompatible with React 18
        <ReactSelectComponents.NoOptionsMessage {...props} className={styles.messageContainer}>
          {t("helpers_no_results")}
        </ReactSelectComponents.NoOptionsMessage>
      );
    },
    [customNoResultElement, minimumLettersToSearch, t]
  );

  const LoadingMessage = useCallback(
    (props: NoticeProps<Option, IsMulti>) => (
      // @ts-expect-error - react-select types incompatible with React 18
      <ReactSelectComponents.LoadingMessage {...props} className={styles.messageContainer} data-testid="select-loading-message">
        {t("search_person.loading")}
      </ReactSelectComponents.LoadingMessage>
    ),
    [t]
  );

  const commonProps = useMemo(
    () => ({
      components: {
        NoOptionsMessage: NoOptionsMessageElement,
        Control,
        DropdownIndicator,
        Option: OptionComponent,
        Menu,
        SingleValue,
        Input,
        LoadingMessage,
        MultiValueContainer,
        MultiValueRemove,
      },
      styles: customStyles,
      onChange: onChange as ((newValue: OnChangeValue<Option, IsMulti>, actionMeta: ActionMeta<Option>) => void) | undefined,
      onMenuOpen: () => {
        const arrowIndicator = document.getElementById(dropdownIndicatorIdRef.current);
        arrowIndicator?.classList?.add(styles.rotate);
      },
      onMenuClose: () => {
        const arrowIndicator = document.getElementById(dropdownIndicatorIdRef.current);
        arrowIndicator?.classList?.remove(styles.rotate);
      },
      className: classnames(globalStyles[`input-${selectSize ?? "default"}`], { [styles.multipicklist]: isMulti }),
      isMulti,
      formatOptionLabel: (data: Option, labelMeta: FormatOptionLabelMeta<Option>) => {
        return formattedLabel(data, labelMeta);
      },
      isDisabled: disabled,
      isClearable: showClear ?? false,
      placeholder,
      name,
      defaultValue,
    }),
    [
      Control,
      DropdownIndicator,
      Input,
      LoadingMessage,
      Menu,
      MultiValueContainer,
      MultiValueRemove,
      NoOptionsMessageElement,
      OptionComponent,
      SingleValue,
      customStyles,
      defaultValue,
      disabled,
      formattedLabel,
      isMulti,
      name,
      onChange,
      placeholder,
      selectSize,
      showClear,
    ]
  );

  const formatCreateLabelToUse = (inputValue: string) => {
    if (formatCreateLabel) {
      return formatCreateLabel(inputValue);
    }

    if (createLabel) {
      return (
        <div className={styles.createLabel}>
          {createLabelIcon && (
            <Svg iconName={createLabelIcon} className={classnames(globalStyles[`color-${createLabelIconColor ?? "brand-tertiary"}`])} height={18} />
          )}{" "}
          {createLabel}
        </div>
      );
    }
    return <div>{<>{inputValue.length > 0 ? `${t("actions_create_button")} "${inputValue}"` : t("actions_create_new")}</>}</div>;
  };

  const stopLoadOptionsIfValueIsLessThenMinimumLetters = useCallback(
    async (inputValue: string) => {
      if (minimumLettersToSearch && minimumLettersToSearch > inputValue.length) {
        return {
          options: [],
        };
      }
      if (!loadOptionsFromServerFunction) {
        return {
          options: [],
        };
      }

      return await loadOptionsFromServerFunction(inputValue);
    },
    [loadOptionsFromServerFunction, minimumLettersToSearch]
  );

  return (
    <div
      className={classnames(
        styles.inputContainer,
        globalStyles[`p-${padding}`],
        globalStyles[`pi-${paddingInline}`],
        globalStyles[`pt-${paddingTop}`],
        globalStyles[`pr-${paddingRight}`],
        globalStyles[`pb-${paddingBottom}`],
        globalStyles[`pl-${paddingLeft}`],
        globalStyles[`m-${margin}`],
        globalStyles[`mt-${marginTop}`],
        globalStyles[`mr-${marginRight}`],
        globalStyles[`mb-${marginBottom}`],
        globalStyles[`ml-${marginLeft}`],
        globalStyles[`fs-default`],
        { [globalStyles.disabled]: disabled }
      )}
      data-testid={`${testIdMainProps}-container`}
    >
      {isAsync && isCraeteable && loadOptionsFromServerFunction ? (
        <AsyncPaginateCreatable
          {...commonProps}
          loadOptions={stopLoadOptionsIfValueIsLessThenMinimumLetters}
          debounceTimeout={500}
          value={value}
          allowCreateWhileLoading={false}
          formatCreateLabel={formatCreateLabelToUse}
          onBlur={onBlur}
          onCreateOption={onCreateOption}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          isValidNewOption={isValidNewOption as any}
        />
      ) : isAsync && loadOptionsFromServerFunction ? (
        <AsyncPaginate
          defaultOptions={options}
          {...commonProps}
          classNames={classNames}
          loadOptions={stopLoadOptionsIfValueIsLessThenMinimumLetters}
          value={value}
          onBlur={onBlur}
          defaultValue={defaultValue}
          debounceTimeout={500}
        />
      ) : isCraeteable ? (
        <CreatableSelect
          {...commonProps}
          createOptionPosition={"last"}
          onCreateOption={onCreateOption}
          options={options}
          onBlur={onBlur}
          value={value}
          formatCreateLabel={formatCreateLabelToUse}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          isValidNewOption={isValidNewOption as any}
        />
      ) : (
        <ReactSelect
          ref={reactSelectRef}
          options={options}
          {...commonProps}
          classNames={classNames}
          onBlur={onBlur}
          value={value}
          isSearchable={Boolean(isSearchable)}
          menuShouldScrollIntoView
          menuPortalTarget={mainProps.menuPortalTarget}
        />
      )}
    </div>
  );
};

export const Select = <
  OptionValueType,
  IsMulti extends boolean = false,
  Option extends SelectOption<OptionValueType> = SelectOption<OptionValueType>,
>({
  ...props
}: SelectProps<OptionValueType, IsMulti, Option>) => {
  return <BaseSelectDoNotUse {...props} />;
};

export const AsyncSelect = <
  OptionValueType,
  IsMulti extends boolean = false,
  Option extends SelectOption<OptionValueType> = SelectOption<OptionValueType>,
>({
  ...props
}: AsyncSelectProps<OptionValueType, IsMulti, Option>) => {
  return <BaseSelectDoNotUse {...props} isAsync />;
};

export const CreateableSelect = <
  OptionValueType,
  IsMulti extends boolean = false,
  Option extends SelectOption<OptionValueType> = SelectOption<OptionValueType>,
>({
  ...props
}: CreateableSelectProps<OptionValueType, IsMulti, Option>) => {
  return <BaseSelectDoNotUse {...props} isCraeteable />;
};

export const CreateableAsyncSelect = <
  OptionValueType,
  IsMulti extends boolean = false,
  Option extends SelectOption<OptionValueType> = SelectOption<OptionValueType>,
>({
  ...props
}: CreateableAsyncSelectProps<OptionValueType, IsMulti, Option>) => {
  return <BaseSelectDoNotUse {...props} isAsync isCraeteable={!props.disableCreate} />;
};

const menuDirectionStyles = {
  bottom: {
    top: "100%",
    bottom: "auto",
  },
  top: {
    top: "auto",
    bottom: "100%",
  },
};

function getMenuPlacement(selectElement: HTMLDivElement | null, currentDirection: "bottom" | "top") {
  if (!selectElement) return "bottom";
  const rect = selectElement.getBoundingClientRect();
  const dropdownMenuHeight = selectElement.clientHeight;

  const [currentDirectionRect, otherDirectionRect] = currentDirection === "bottom" ? [rect.bottom, rect.top] : [rect.top, rect.bottom];
  const otherDirection = currentDirection === "top" ? "bottom" : "top";

  const currentDirectionSpace = window.innerHeight - currentDirectionRect;
  const otherDirectionSpace =
    currentDirection === "bottom" ? otherDirectionRect - dropdownMenuHeight : window.innerHeight - (otherDirectionRect + dropdownMenuHeight);

  const noSpaceInCurrentDirection = currentDirectionSpace < dropdownMenuHeight;
  const enoughSpaceInOtherDirection = otherDirectionSpace >= 0;

  return noSpaceInCurrentDirection && enoughSpaceInOtherDirection ? otherDirection : currentDirection;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const CustomMenuComponent: <T extends MenuProps<any, any>>(
  props: T & { menuBottomText?: string; menuWidth?: string; menuClassName?: string; ignoreMenuDirection?: boolean }
) => React.JSX.Element = (props) => {
  const { children, className, getStyles, innerProps, menuWidth, menuBottomText, menuClassName, innerRef, ignoreMenuDirection } = props;
  const [menuDirection, setMenuDirection] = useState<"bottom" | "top">("bottom");

  useLayoutEffect(() => {
    if (!innerRef || typeof innerRef !== "object") return;
    // Fix the direction by space
    const newDirection = getMenuPlacement(innerRef.current, menuDirection);
    if (newDirection !== menuDirection && !ignoreMenuDirection) setMenuDirection(newDirection);

    // React select has bug that makes it not focus on selected element so it has to be done by query
    const selectedOptionElement = innerRef.current?.querySelector(`.${styles.selectedOption}`);
    selectedOptionElement?.scrollIntoView({
      behavior: "smooth", // DO NOT REMOVE, important for screen scroll safety
      block: "nearest", // Together with the smooth behavior it fix the screen jump to the element instead of the menu jump to the element
    });
  }, [ignoreMenuDirection, innerRef, menuDirection]);
  return (
    <div
      data-testid={"select-menu"}
      data-is-select-menu={true}
      ref={innerRef}
      style={{
        ...omit(getStyles("menu", props), "borderColor", "backgroundColor", "width"),
        width: menuWidth ?? "100%",
        ...menuDirectionStyles[menuDirection],
      }}
      className={classnames(className, styles.menu, globalStyles["bc-neutral-secondary"], globalStyles["bg-color-main-secondary"], menuClassName)}
      {...innerProps}
    >
      <span>{children}</span>
      {menuBottomText && <div className={styles.menuBottomText}>{menuBottomText}</div>}
    </div>
  );
};
